import React, { useState, useEffect } from 'react';
import { MailingListsNoiseChart } from './MailingListsNoiseChart';
import { LoginsChart } from './LoginsChart';
import { SubscriptionsChart } from './SubscriptionsChart';
import { BreachesChart } from './BreachesChart';

type TabKey = 'mailing' | 'logins' | 'subscriptions' | 'breaches';
type WindowMode = 'normal' | 'minimized' | 'fullscreen';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'mailing', label: 'Mailing Lists' },
  { key: 'logins', label: 'Logins' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'breaches', label: 'Breaches' },
];

// ========== Traffic Lights ==========

type TrafficKind = 'close' | 'minimize' | 'maximize';

const TRAFFIC_COLORS: Record<TrafficKind, string> = {
  close: '#FF5F57',
  minimize: '#FEBC2E',
  maximize: '#28C840',
};

// macOS-style hover glyphs — rendered inside the traffic light circles
const TrafficGlyph: React.FC<{ kind: TrafficKind }> = ({ kind }) => {
  if (kind === 'close') {
    return (
      <svg width="8" height="8" viewBox="0 0 7 7" style={{ display: 'block' }}>
        <path d="M1.3 1.3L5.7 5.7 M5.7 1.3L1.3 5.7" stroke="#4D0001" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'minimize') {
    return (
      <svg width="8" height="8" viewBox="0 0 7 7" style={{ display: 'block' }}>
        <path d="M1 3.5H6" stroke="#5E3B00" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    );
  }
  // maximize — two diagonal arrows (macOS fullscreen style)
  return (
    <svg width="8" height="8" viewBox="0 0 10 10" style={{ display: 'block' }}>
      <path d="M1.5 6L1.5 8.5L4 8.5" stroke="#003D05" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M8.5 4L8.5 1.5L6 1.5" stroke="#003D05" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
};

const TrafficLight: React.FC<{
  kind: TrafficKind;
  groupHovered: boolean;
  onClick: () => void;
  title: string;
}> = ({ kind, groupHovered, onClick, title }) => (
  <button
    aria-label={title}
    title={title}
    onClick={onClick}
    style={{
      width: 12,
      height: 12,
      borderRadius: '50%',
      background: TRAFFIC_COLORS[kind],
      boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.1)',
      border: 'none',
      padding: 0,
      margin: 0,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: groupHovered ? 1 : 1,
      transition: 'transform 100ms ease-out',
    }}
    onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.92)'; }}
    onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
  >
    <span style={{
      opacity: 1,
      transition: 'opacity 120ms ease-out',
      display: 'flex',
    }}>
      <TrafficGlyph kind={kind} />
    </span>
  </button>
);

// ========== Minimized Dock Pill ==========

const MinimizedDock: React.FC<{ onRestore: () => void }> = ({ onRestore }) => {
  const [hover, setHover] = useState(false);
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      fontFamily: "'Inter', -apple-system, sans-serif",
      animation: 'dockPillEnter 400ms cubic-bezier(0.2, 0.9, 0.3, 1.1)',
      overflow: 'hidden',
    }}>
      <button
        onClick={onRestore}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 16px 10px 10px',
          background: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: '14px',
          color: 'white',
          fontFamily: 'inherit',
          fontSize: '13px',
          letterSpacing: '-0.01em',
          cursor: 'pointer',
          boxShadow: '0 12px 32px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.25)',
          transform: hover ? 'translateY(-2px)' : 'translateY(0)',
          transition: 'transform 180ms cubic-bezier(0.2, 0.9, 0.3, 1.1), background 180ms ease-out',
        }}
      >
        {/* Mini window preview */}
        <div style={{
          width: 36,
          height: 26,
          borderRadius: 5,
          background: '#F5F7FA',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          flexShrink: 0,
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 6,
            background: 'linear-gradient(180deg, #E8E6E8 0%, #D6D3D6 100%)',
            borderBottom: '0.5px solid #B5B2B5',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 2,
            gap: 1.5,
          }}>
            <div style={{ width: 2, height: 2, borderRadius: '50%', background: '#FF5F57' }} />
            <div style={{ width: 2, height: 2, borderRadius: '50%', background: '#FEBC2E' }} />
            <div style={{ width: 2, height: 2, borderRadius: '50%', background: '#28C840' }} />
          </div>
          {/* Fake chart bar */}
          <div style={{
            position: 'absolute', bottom: 4, left: 4, right: 4, height: 3,
            display: 'flex', gap: 0.5,
          }}>
            <div style={{ flex: 3.5, background: '#3E19A4', borderRadius: 1 }} />
            <div style={{ flex: 3.3, background: '#4A3CF3', borderRadius: 1 }} />
            <div style={{ flex: 2.1, background: '#2169AC', borderRadius: 1 }} />
            <div style={{ flex: 1.1, background: '#8D63FF', borderRadius: 1 }} />
            <div style={{ flex: 1.1, background: '#9432F5', borderRadius: 1 }} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <span style={{ fontWeight: 600 }}>yorba dataviz</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 400 }}>
            {hover ? 'click to restore' : 'minimized'}
          </span>
        </div>
      </button>
      <style>{`
        @keyframes dockPillEnter {
          from { opacity: 0; transform: scale(1.35); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

// ========== Close Confirmation Dialog ==========

const CloseDialog: React.FC<{ onCancel: () => void; onConfirm: () => void }> = ({ onCancel, onConfirm }) => {
  const [compact, setCompact] = useState(
    () => window.innerWidth < 520 || window.innerHeight < 580
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    const onResize = () => setCompact(window.innerWidth < 520 || window.innerHeight < 580);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [onCancel]);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10, 15, 30, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'dialogBackdropFade 150ms ease-out',
        padding: compact ? '16px' : '20px',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '14px',
          padding: compact ? '18px 20px 16px' : '26px 28px 20px',
          maxWidth: '440px',
          width: '100%',
          fontFamily: "'Inter', -apple-system, sans-serif",
          boxShadow: '0 24px 60px rgba(0,0,0,0.35), 0 4px 12px rgba(0,0,0,0.2)',
          animation: 'dialogCardEnter 220ms cubic-bezier(0.2, 0.9, 0.2, 1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: compact ? '8px' : '12px' }}>
          <span style={{ fontSize: compact ? 18 : 22 }} role="img" aria-label="thinking">🤨</span>
          <span style={{
            fontSize: compact ? 15 : 17,
            fontWeight: 600,
            color: '#1F2933',
            letterSpacing: '-0.01em',
          }}>
            Hold on a second.
          </span>
        </div>
        <div style={{
          fontSize: compact ? 13 : 14,
          lineHeight: 1.5,
          color: '#52525B',
          marginBottom: compact ? 16 : 22,
        }}>
          You're about to close a case study — the thing you came here to read.
          That's a bit like walking out of a movie during the opening credits.
          Technically allowed. Mildly confusing.
        </div>
        <div style={{
          display: 'flex',
          flexDirection: compact ? 'column' : 'row',
          gap: '8px',
          justifyContent: compact ? 'stretch' : 'flex-end',
        }}>
          {/* In compact mode the primary action goes on top */}
          {compact && (
            <button
              onClick={onCancel}
              autoFocus
              style={{
                padding: '10px 18px',
                background: '#1F2933',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '-0.01em',
                transition: 'background 140ms ease-out',
                width: '100%',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#323F4B'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1F2933'; }}
            >
              Fine, I'll keep reading
            </button>
          )}
          <button
            onClick={onConfirm}
            style={{
              padding: compact ? '9px 16px' : '9px 16px',
              background: 'transparent',
              border: '1px solid #E4E7EB',
              borderRadius: '8px',
              color: '#616E7C',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '-0.01em',
              transition: 'background 140ms ease-out',
              width: compact ? '100%' : 'auto',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F5F7FA'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            Close anyway
          </button>
          {!compact && (
            <button
              onClick={onCancel}
              autoFocus
              style={{
                padding: '9px 18px',
                background: '#1F2933',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '-0.01em',
                transition: 'background 140ms ease-out',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#323F4B'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1F2933'; }}
            >
              Fine, I'll keep reading
            </button>
          )}
        </div>
      </div>
      <style>{`
        @keyframes dialogBackdropFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes dialogCardEnter {
          from { opacity: 0; transform: scale(0.94) translateY(6px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

// ========== Closed / Empty State ==========

const ClosedView: React.FC<{ onReopen: () => void }> = ({ onReopen }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    background: 'linear-gradient(135deg, #0a0a14 0%, #0c1420 50%, #081628 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '20px',
    fontFamily: "'Inter', -apple-system, sans-serif",
    animation: 'closedFadeIn 260ms ease-out',
  }}>
    <div style={{
      color: 'rgba(255,255,255,0.55)',
      fontSize: 15,
      letterSpacing: '-0.01em',
      textAlign: 'center',
      lineHeight: 1.5,
      maxWidth: 360,
    }}>
      Well. That was a brief visit.
      <br />
      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }}>
        The case study is closed. Hope you enjoyed the 3 seconds.
      </span>
    </div>
    <button
      onClick={onReopen}
      style={{
        padding: '11px 22px',
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '10px',
        color: 'white',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        fontFamily: 'inherit',
        letterSpacing: '-0.01em',
        transition: 'background 160ms ease-out, transform 160ms ease-out',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.background = 'rgba(255,255,255,0.16)';
        el.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.background = 'rgba(255,255,255,0.1)';
        el.style.transform = 'translateY(0)';
      }}
    >
      Oh fine, take me back
    </button>
    <style>{`
      @keyframes closedFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `}</style>
  </div>
);

// ========== Main App ==========

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('mailing');
  const [windowMode, setWindowMode] = useState<WindowMode>('normal');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closed, setClosed] = useState(false);
  const [lightsHovered, setLightsHovered] = useState(false);

  // Notify the parent case study page about mode changes so it can
  // remove the min-width on the scroll wrapper for overlay states.
  useEffect(() => {
    const mode = closed ? 'closed' : closeDialogOpen ? 'dialog' : windowMode;
    try {
      window.parent.postMessage({ type: 'yorba-prototype-mode', mode }, '*');
    } catch (e) {}
  }, [windowMode, closed, closeDialogOpen]);

  if (closed) {
    return (
      <ClosedView
        onReopen={() => {
          setClosed(false);
          setWindowMode('normal');
        }}
      />
    );
  }

  if (windowMode === 'minimized') {
    return <MinimizedDock onRestore={() => setWindowMode('normal')} />;
  }

  const isFull = windowMode === 'fullscreen';

  return (
    <div style={{
      display: 'flex',
      alignItems: isFull ? 'stretch' : 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      fontFamily: "'Inter', -apple-system, sans-serif",
      padding: isFull ? '0' : '30px 40px',
      transition: 'padding 320ms cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* macOS Window Chrome */}
      <div style={{
        width: '100%',
        maxWidth: isFull ? '100%' : '1280px',
        borderRadius: isFull ? '0' : '12px',
        overflow: 'hidden',
        boxShadow: isFull ? 'none' : '0 25px 60px rgba(0, 0, 0, 0.4), 0 8px 20px rgba(0, 0, 0, 0.3)',
        transition: 'max-width 320ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 260ms ease-out, box-shadow 260ms ease-out',
      }}>
        {/* Title Bar */}
        <div style={{
          background: 'linear-gradient(180deg, #E8E6E8 0%, #D6D3D6 100%)',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '14px',
          paddingRight: '14px',
          position: 'relative',
          borderBottom: '1px solid #B5B2B5',
        }}>
          {/* Traffic Lights */}
          <div
            onMouseEnter={() => setLightsHovered(true)}
            onMouseLeave={() => setLightsHovered(false)}
            style={{ display: 'flex', gap: '8px' }}
          >
            <TrafficLight
              kind="close"
              groupHovered={lightsHovered}
              onClick={() => setCloseDialogOpen(true)}
              title="Close"
            />
            <TrafficLight
              kind="minimize"
              groupHovered={lightsHovered}
              onClick={() => setWindowMode('minimized')}
              title="Minimize"
            />
            <TrafficLight
              kind="maximize"
              groupHovered={lightsHovered}
              onClick={() => setWindowMode(isFull ? 'normal' : 'fullscreen')}
              title={isFull ? 'Exit full screen' : 'Enter full screen'}
            />
          </div>
          {/* URL Bar */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            borderRadius: '6px',
            padding: '4px 16px',
            fontSize: '12px',
            color: '#52525B',
            minWidth: '280px',
            textAlign: 'center',
            border: '1px solid #C8C5C8',
            letterSpacing: '0.01em',
          }}>
            <span style={{ color: '#A1A1AA' }}>https://</span>
            yorba.app
            <span style={{ color: '#A1A1AA' }}>/accounts/{activeTab === 'mailing' ? 'mailing-lists' : activeTab === 'logins' ? 'logins' : activeTab === 'subscriptions' ? 'subscriptions' : 'breaches'}</span>
          </div>
        </div>

        {/* Browser Content */}
        <div style={{
          background: '#F5F7FA',
          padding: '40px 48px',
          minHeight: isFull ? 'calc(100vh - 40px)' : 'auto',
          // Chart cards read this var to decide their max-width.
          // In fullscreen we let them fill the viewport minus padding.
          ['--chart-max-width' as any]: isFull ? '100%' : '1180px',
        } as React.CSSProperties}>
          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px',
          }}>
            <div style={{
              display: 'flex',
              gap: '4px',
              background: '#ECEDF0',
              borderRadius: '10px',
              padding: '4px',
            }}>
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      background: isActive ? 'white' : 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px 20px',
                      fontSize: '14px',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#1F2933' : '#616E7C',
                      cursor: 'pointer',
                      fontFamily: "'Inter', -apple-system, sans-serif",
                      letterSpacing: '-0.01em',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' : 'none',
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {activeTab === 'mailing' && <MailingListsNoiseChart />}
          {activeTab === 'logins' && <LoginsChart />}
          {activeTab === 'subscriptions' && <SubscriptionsChart />}
          {activeTab === 'breaches' && <BreachesChart />}
        </div>
      </div>

      {closeDialogOpen && (
        <CloseDialog
          onCancel={() => setCloseDialogOpen(false)}
          onConfirm={() => {
            setCloseDialogOpen(false);
            setClosed(true);
          }}
        />
      )}
    </div>
  );
};

export default App;
